import { useState, ChangeEventHandler, FormEventHandler } from "react";
import { signIn, fetchUserAttributes, signOut } from "aws-amplify/auth";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

// Redux
import { actions } from "@/redux/reducers/User";

// API's
import { getLoginUser, login } from "@/Auth/api/Login/queries";

// Components
import LoginContainer from "@/Auth/Layouts/LoginContainer";

// MUI
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	FormGroup,
	Container,
	Stack,
	Typography,
	TextField,
	Grid,
} from "@mui/material";

// Icons
import Email from "@/Auth/assets/icons/email.svg";
import Lock from "@/Auth/assets/icons/lock.svg";
import Hidden from "@/Auth/assets/icons/hidden.svg";
import Visible from "@/Auth/assets/icons/visible.svg";

import { tenant } from "@/Constants/globalConfig";
import EndAdornment from "../components/EndAdornment";
import StartAdornment from "../components/StartAdornment";

const message = "The username or password you entered is incorrect.";

type LoginState = {
	username: string;
	password: string;
	someEmptyField: boolean;
	remember: boolean;
	showPassword: boolean;
	error: boolean;
	isLoginLoading: boolean;
	message: string;
};

type ResolveDisableParams = { isLoading: boolean; isLoadingUser: boolean; state: LoginState };

const resolveIsDisable = ({ isLoading, isLoadingUser, state }: ResolveDisableParams) => {
	const isLoadingCheck = isLoading || isLoadingUser;
	return isLoadingCheck || state.someEmptyField || state.isLoginLoading || false;
};

const DASHBOARD_ROUTE = "/dashboard";

export default function Login({ tabId, messageMethod }: Readonly<{ tabId: string; messageMethod: Function }>) {
	const navigate = useNavigate();

	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const [userId, setUserId] = useState<string | null>(null);
	const [locationId, setLocationId] = useState(null);

	const [state, setState] = useState<LoginState>({
		username: "",
		password: "",
		someEmptyField: true,
		remember: false,
		showPassword: false,
		error: false,
		isLoginLoading: false,
		message,
	});

	const { isLoading: isLoadingUser } = useQuery({
		queryKey: ["userToLogin", userId],
		queryFn: async () => {
			const userLogged = await getLoginUser(tenant, userId);
			setLocationId(userLogged?.preferences?.landingLocation || userLogged.locationId);
			queryClient.removeQueries({ queryKey: ["userToLogin"] });
			return userLogged;
		},
		enabled: !!userId,
	});

	const { isLoading } = useQuery({
		queryKey: ["loggedUser"],
		queryFn: async () => {
			const logged = await login(locationId, state.username);
			if (logged) {
				setState((oldValues) => ({
					...oldValues,
					error: false,
				}));

				// Set User in redux & localStorage
				dispatch(actions.SET_USER({ user: logged }));
				messageMethod({ tabId, logged, loggedIn: true });
				navigate(logged?.preferences?.landingPage || DASHBOARD_ROUTE);
			} else {
				setState((oldValues) => ({
					...oldValues,
					message,
					error: true,
				}));
			}

			return logged;
		},
		enabled: !!locationId,
	});

	const handleChangeUser: ChangeEventHandler<HTMLInputElement> = (e) => {
		let someEmptyField = true;
		if (e.target.value && state.password) {
			someEmptyField = false;
		}

		setState((oldValues) => ({
			...oldValues,
			someEmptyField,
			username: e.target.value,
		}));
	};

	const handleChangePass: ChangeEventHandler<HTMLInputElement> = (e) => {
		let someEmptyField = true;
		if (e.target.value && state.username) {
			someEmptyField = false;
		}

		setState({
			...state,
			someEmptyField,
			password: e.target.value,
		});
	};

	const handleChangeRemember = () => {
		setState({
			...state,
			remember: !state.remember,
		});
	};

	const handleClickShowPassword = () => {
		setState({
			...state,
			showPassword: !state.showPassword,
		});
	};

	const handleSignIn = async (username: string, password: string) => {
		try {
			const result = await signIn({
				password,
				username: username.toLowerCase(),
				options: {
					authFlowType: "USER_SRP_AUTH",
				},
			});

			if (result?.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
				navigate("/multi-factor-authentication", {
					state: {
						password,
						username: username.toLowerCase(),
						signInResult: result,
					},
				});
			}
			if (result?.nextStep?.signInStep === "DONE") {
				setState((oldValues) => ({
					...oldValues,
					username: username.toLowerCase(),
				}));

				const userAttributes = await fetchUserAttributes();
				setUserId(userAttributes?.["custom:_id"] ?? null);
			}
		} catch (error: unknown) {
			let msg = "Unexpected error. Please try again.";

			const errorMessages: { [key: string]: string } = {
				UserNotFoundException: "Incorrect username or password.",
				NotAuthorizedException: "Incorrect username or password.",
				UserAlreadyAuthenticatedException: "User is already authenticated.",
			};

			if (error instanceof Error && errorMessages[error.name]) {
				msg = errorMessages[error.name];
			}

			setState((oldValues) => ({
				...oldValues,
				message: msg,
				error: true,
				isLoginLoading: false,
			}));
			setUserId("");
			queryClient.clear();
			if (error instanceof Error && error.name === "UserAlreadyAuthenticatedException") {
				signOut();
			}
		}
	};

	const handleSubmit: FormEventHandler = (e) => {
		e.preventDefault();
		window.localStorage.clear();
		setState((oldValues) => ({
			...oldValues,
			error: false,
		}));

		const { username: localUsername, password: localPassword } = state || {};

		setState((oldValues) => ({
			...oldValues,
			isLoginLoading: true,
		}));

		handleSignIn(localUsername, localPassword);
	};

	const isDisabled = resolveIsDisable({ isLoading, isLoadingUser, state });

	return (
		<LoginContainer>
			<form
				className={clsx({
					"form-login form-custom": true,
					"form-error": state.error,
				})}
				noValidate
				onSubmit={handleSubmit}
			>
				<Stack direction="row" className="mx-[13px]">
					<Container className="px-0">
						<Typography className="text-title mb-[36px] mt-0 text-center">Sign In</Typography>
						<Container className="px-0 mb-[13px]">
							<Grid container spacing={4.5}>
								<Grid item xs={12}>
									<TextField
										placeholder="E-mail"
										variant="outlined"
										fullWidth
										required={true}
										className="mt-0"
										autoFocus
										InputProps={{
											className: "text-regular h-[37px] pl-[14px] pb-[4px] mt-0 rounded-[6px]",
											startAdornment: <StartAdornment src={Email} alt="Email icon" />,
										}}
										value={state.username}
										onChange={handleChangeUser}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										placeholder="Password"
										variant="outlined"
										fullWidth
										required={true}
										type={state.showPassword ? "text" : "password"}
										value={state.password}
										onChange={handleChangePass}
										InputProps={{
											className: "text-regular h-[37px] pl-[14px] pb-[4px] mt-0 rounded-[6px]",
											startAdornment: <StartAdornment src={Lock} alt="Lock icon" />,
											endAdornment: (
												<EndAdornment
													handleOnClick={handleClickShowPassword}
													showPassword={state.showPassword}
													iconHidden={Hidden}
													iconVisible={Visible}
												/>
											),
										}}
									/>
								</Grid>
							</Grid>
						</Container>

						<Stack direction="row" justifyContent="space-between">
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={state.remember}
											onChange={handleChangeRemember}
											inputProps={{ "aria-label": "controlled" }}
											size="small"
											color="primary"
										/>
									}
									label={<Typography className="capitalize text-regular">Remember me</Typography>}
								/>
							</FormGroup>

							<FormGroup sx={{ justifyContent: "center" }}>
								<Link
									to={`/reset/${state.username}`}
									className={clsx({
										"capitalize font-montserrat text-regular underline": true,
										"!text-[color:--neutral-gray] pointer-events-none": isLoading || isLoadingUser,
									})}
								>
									Forgot Password?
								</Link>
							</FormGroup>
						</Stack>
					</Container>
				</Stack>

				<Stack className="text-center" sx={{ padding: "0px 30px", minHeight: "30px", display: state.error ? "block" : "none" }}>
					<span className="alert-message">{state.message}</span>
				</Stack>

				<Stack direction="row" justifyContent="center" alignContent="center" className="mx-[13px] mt-[24px]">
					<Button variant="contained" className="btn-submit" type="submit" disabled={isDisabled} disableElevation>
						{isLoading || isLoadingUser || state.isLoginLoading ? <CircularProgress color="inherit" size={25} /> : "Sign In"}
					</Button>
				</Stack>
			</form>
		</LoginContainer>
	);
}
